import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import QRCode from 'qrcode';
import { navigate, useStaticQuery, graphql } from 'gatsby';
import { Modal, Alert } from '@pella/sash-framework';
import LoadingOverlay from '../../../common/loading-overlay';
import { useSelector } from 'react-redux';
import { getSessionStorage, setSessionStorage, getLocalStorage } from '../../../utilities/functions';
import { initVisualJourney } from '../../../../actions/rta-actions';

const VisualJourneyModal = ({ isOpen, onClose, redirect }) => {
	const data = useStaticQuery(query).contentstackTechsee;
	const locations = useSelector((state) => state.locations);
	const [error, setError] = useState(null);
	const [status, setStatus] = useState('');
	const [showQRCode, setShowQRCode] = useState(false);

	useEffect(() => {
		if (isOpen) {
			const branchId = getLocalStorage('UserLocation')?.branch_id || locations?.zone?.retail_branch_id;
			// Quebec branch has opted out of the Visual Journey experience
			const optedOutBranches = ['787', '426'];
			if (optedOutBranches.includes(branchId) || getSessionStorage('journeyOptOut')) {
				navigate(redirect);
			} else {
				setShowQRCode(true);
				startVisualJourney();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [isOpen]);

	const onModalCloseClick = () => {
		setSessionStorage('journeyOptOut', true);
		if (typeof onClose === 'function') {
			onClose();
		}
		if (redirect) {
			navigate(redirect);
		}
	};

	const startVisualJourney = async () => {
		setError(null);

		const res = await initVisualJourney();
		if (res?.data?.sessionLink) {
			if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
				navigate(res.data.sessionLink);
			} else {
				try {
					await createQrCode(res.data.sessionLink);
				} catch (err) {
					setError('Something went wrong, please try again');
					setStatus('');
				}
			}
		} else {
			setStatus('');
			if (res?.data?.error?.message) {
				setError(res.data.error.message);
			}
		}
	};

	const createQrCode = async (url) => {
		const qrcode = await QRCode.toCanvas(url, { scale: 5 });
		const div = document.getElementById('qrcode-canvas');
		div.appendChild(qrcode);
		setStatus('');
	};

	return (
		showQRCode && (
			<Modal
				isOpen={isOpen}
				onClose={onModalCloseClick}
				width={460}
				className='p-6 [&>button>span]:bg-white [&>button>span]:text-4xl/none [&>button>span]:text-black [&>button]:right-5 [&>button]:h-auto [&>button]:w-auto'
			>
				<Modal.Header
					className='mb-3 !px-0 !pb-3 !pt-0'
					headingLevel={3}
				>
					{data?.postal_code?.modal_header}
				</Modal.Header>

				<Modal.Content className='!p-0'>
					{status === 'processing' && <LoadingOverlay fixed={false} />}
					{error && (
						<Alert
							className='mb-4'
							type='error'
							hideClose
						>
							{error}
						</Alert>
					)}

					{showQRCode && (
						<>
							{data?.postal_code?.description && (
								<div
									className='[&>p]:text-subtitle [&>p]:mb-2 [&>p]:font-bold [&>ul]:m-0 [&>ul]:pl-7'
									dangerouslySetInnerHTML={{ __html: data?.postal_code?.description }}
								/>
							)}

							<div
								className='[&>canvas]:mx-auto'
								id='qrcode-canvas'
							/>

							<p className='text-pretty text-center'>{data?.qr_code}</p>

							<button
								onClick={onModalCloseClick}
								className='mt-4 w-full text-center font-semibold text-black md:mt-6 [&_span]:font-normal [&_span]:underline [&_span]:hover:no-underline'
								dangerouslySetInnerHTML={{ __html: data?.opt_out }}
								aria-label='Opt out of Visual Journey'
							/>
						</>
					)}
				</Modal.Content>
			</Modal>
		)
	);
};

VisualJourneyModal.propTypes = {
	onClose: PropTypes.func,
	isOpen: PropTypes.bool,
	redirect: PropTypes.string,
};

export default VisualJourneyModal;

export const query = graphql`
	{
		contentstackTechsee {
			postal_code {
				modal_header
				description
			}
			qr_code
			opt_out
		}
	}
`;
